import type { ItemWriting } from "./yufa-written-item.js";
import type { GrammarTeaching } from "./yufa-teaching.js";
import { teachingMatches } from "./yufa-teaching.js";
import { rewriteKey } from "../../fuxi/grade-rewrite.js";
import { recordOf, writingOf, writingsOf } from "./yufa-writing-read.js";

/** An explicit empty choice has its own value and never means no submission. */
export type ContrastOption =
  | { readonly kind: "word"; readonly word: ItemWriting }
  | { readonly kind: "nothing" };

export interface ContrastBody {
  readonly kind: "contrast";
  readonly before: ItemWriting;
  readonly after: ItemWriting;
  readonly options: readonly ContrastOption[];
  readonly accepted: readonly number[];
}

/** Positions are boundaries in the fixed pieces, before the piece at that index. */
export interface PlacementBody {
  readonly kind: "placement";
  readonly word: ItemWriting;
  readonly pieces: readonly ItemWriting[];
  readonly positions: readonly number[];
  readonly accepted: readonly number[];
}

export type FocusedBody = ContrastBody | PlacementBody;

function join(parts: readonly ItemWriting[]): ItemWriting {
  return {
    simplified: parts.map((part) => part.simplified).join(""),
    traditional: parts.map((part) => part.traditional).join(""),
  };
}

/** Reconstruct both scripts without trimming, tokenizing or moving punctuation. */
export function focusedForms(item: FocusedBody): readonly ItemWriting[] {
  return item.kind === "contrast"
    ? item.options.map((option) =>
        join([
          item.before,
          ...(option.kind === "nothing" ? [] : [option.word]),
          item.after,
        ]),
      )
    : item.positions.map((at) =>
        join([
          ...item.pieces.slice(0, at),
          item.word,
          ...item.pieces.slice(at),
        ]),
      );
}

/** The selected values remain stable across scripts and generated readings. */
export function focusedValues(item: FocusedBody): readonly number[] {
  return item.kind === "contrast"
    ? item.options.map((_, at) => at)
    : item.positions;
}

/** Return the exact completed sentences approved for the requested meaning. */
export function focusedTargets(item: FocusedBody): readonly ItemWriting[] {
  const values = focusedValues(item);
  return focusedForms(item).filter((_, at) =>
    item.accepted.includes(values[at]!),
  );
}

function fragment(value: unknown): ItemWriting | undefined {
  const record = recordOf(value);
  return typeof record?.["simplified"] === "string" &&
    typeof record["traditional"] === "string"
    ? { simplified: record["simplified"], traditional: record["traditional"] }
    : undefined;
}

function indexes(value: unknown): value is readonly number[] {
  return (
    Array.isArray(value) &&
    value.length > 0 &&
    value.every((at) => Number.isInteger(at) && at >= 0) &&
    new Set(value).size === value.length
  );
}

/** Reject malformed boundaries, implicit empty options and colliding completed forms. */
export function focusedBodyOf(value: unknown): FocusedBody | undefined {
  const record = recordOf(value);
  if (record === undefined || !indexes(record["accepted"])) {
    return undefined;
  }
  const accepted = record["accepted"];
  let body: FocusedBody;
  if (record["kind"] === "contrast") {
    const before = fragment(record["before"]);
    const after = fragment(record["after"]);
    const options = Array.isArray(record["options"])
      ? record["options"].map((value: unknown): ContrastOption | undefined => {
          const option = recordOf(value);
          if (option?.["kind"] === "nothing" && option["word"] === undefined) {
            return { kind: "nothing" };
          }
          const word = writingOf(option?.["word"]);
          return option?.["kind"] === "word" &&
            word !== undefined &&
            /^\p{Script=Han}+$/u.test(word.simplified) &&
            /^\p{Script=Han}+$/u.test(word.traditional)
            ? { kind: "word", word }
            : undefined;
        })
      : [];
    if (
      before === undefined ||
      after === undefined ||
      options.length < 2 ||
      options.length > 4 ||
      !options.every((option) => option !== undefined)
    ) {
      return undefined;
    }
    body = { kind: "contrast", before, after, options, accepted };
  } else if (record["kind"] === "placement") {
    const word = writingOf(record["word"]);
    const pieces = writingsOf(record["pieces"]);
    const positions = record["positions"];
    if (
      word === undefined ||
      !/^\p{Script=Han}+$/u.test(word.simplified) ||
      !/^\p{Script=Han}+$/u.test(word.traditional) ||
      pieces === undefined ||
      pieces.length < 2 ||
      !indexes(positions) ||
      positions.length < 2 ||
      positions.some(
        (at, index) =>
          at > pieces.length ||
          (index > 0 && at <= positions[index - 1]!) ||
          (at > 0 && /[。！？；，、：]$/u.test(pieces[at - 1]!.simplified)),
      )
    ) {
      return undefined;
    }
    body = { kind: "placement", word, pieces, positions, accepted };
  } else {
    return undefined;
  }
  const values = focusedValues(body);
  const forms = focusedForms(body);
  if (
    accepted.some((at) => !values.includes(at)) ||
    ["simplified", "traditional"].some((script) => {
      const keys = forms.map((form) =>
        rewriteKey(form[script as keyof ItemWriting]),
      );
      return keys.includes("") || new Set(keys).size !== keys.length;
    })
  ) {
    return undefined;
  }
  return body;
}

/** Every offered completion needs reviewed feedback and an explicit teaching task. */
export function focusedTeachingMatches(
  item: FocusedBody,
  teaching: GrammarTeaching | undefined,
): boolean {
  if (
    teaching === undefined ||
    teaching.task !== (item.kind === "contrast" ? "select" : "place")
  ) {
    return false;
  }
  const values = focusedValues(item);
  const other = focusedForms(item).filter(
    (_, at) => !item.accepted.includes(values[at]!),
  );
  return teachingMatches(teaching, focusedTargets(item), other);
}
